import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useEffect, useMemo, useState } from 'react';
import { MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from 'react-leaflet';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  HairlineCard,
  Icons,
  iconSize,
  Link,
  MenuItem,
  palette,
  Slider,
  Stack,
  StatusChip,
  TextField,
  Typography,
} from '@munifocus/ui';
import type { TrackPoint } from '@munifocus/contracts-gov';
import { AppShell } from '../components/AppShell';
import { useOptionalScope } from '../providers/ScopeProvider';
import { useTrack, useTrackedOfficers } from '../lib/tracking-api';
import { localDate, oldestReplayDate, replayAt } from '../lib/team-map';
import { getTileConfig, NB_BOUNDS } from '../lib/map-tiles';
import { OpenOnScope } from '../components/map/OpenOnScope';
import { frenchFirstOfMonth } from '@munifocus/utils';

const DEFAULT_RETENTION_DAYS = 30;

function dotIcon(color: string, size = 14): L.DivIcon {
  return L.divIcon({
    html: `<svg width="${size}" height="${size}" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="7" cy="7" r="6" fill="${color}" stroke="${palette.background.paper}" stroke-width="2"/></svg>`,
    className: 'replay-dot',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function FitTrack({ points }: { points: TrackPoint[] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    const latlngs = points.map((p) => [p.lat, p.lng] as [number, number]);
    if (latlngs.length === 1) map.setView(latlngs[0], 15);
    else map.fitBounds(L.latLngBounds(latlngs), { padding: [40, 40] });
  }, [map, points]);
  return null;
}

/**
 * Replay one officer's day: the track as a line, a scrubber to move along it
 * in time, and the visits they made as markers. Days outside the
 * municipality's retention period are not offered — those positions no longer
 * exist.
 *
 * The scrubber is a slider: arrows step one point, Page Up/Down jump, Home and
 * End go to the ends, and it announces the time at each step.
 */
export function TeamHistory() {
  const { t, i18n } = useTranslation();
  const scope = useOptionalScope();
  const lg = scope?.active?.kind === 'local_government' ? scope.active : null;
  const officers = useTrackedOfficers(lg?.id);
  const [officerId, setOfficerId] = useState('');
  const [date, setDate] = useState(() => localDate());
  const track = useTrack(lg?.id, officerId || undefined, date);
  const [index, setIndex] = useState(0);

  const points = useMemo(() => track.data?.points ?? [], [track.data]);
  // A new day or officer starts the replay at the beginning.
  useEffect(() => setIndex(0), [points]);

  const retention = track.data?.retentionDays ?? DEFAULT_RETENTION_DAYS;
  const minDate = oldestReplayDate(retention);
  const maxDate = localDate();
  const current = replayAt(points, index);
  const tiles = getTileConfig();
  const fmtTime = (iso: string) =>
    frenchFirstOfMonth(
      new Intl.DateTimeFormat(i18n.language.startsWith('fr') ? 'fr-CA' : 'en-CA', {
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(iso)),
    );

  return (
    <AppShell>
      <Stack
        direction="row"
        sx={{ mb: 2, alignItems: 'flex-end', justifyContent: 'space-between', gap: 2 }}
      >
        <Box>
          <Typography variant="h4" sx={{ letterSpacing: '-0.01em' }}>
            {t('gov.team.history.title')}
          </Typography>
          <Typography sx={{ mt: 0.5, color: palette.text.secondary }}>
            {t('gov.team.history.subtitle', { days: retention })}
          </Typography>
        </Box>
        <Button
          component={RouterLink}
          to="/team"
          variant="outlined"
          startIcon={<Icons.Radio size={iconSize.md} aria-hidden />}
        >
          {t('gov.team.history.backToLive')}
        </Button>
      </Stack>

      {!lg && <Alert severity="info">{t('gov.team.pickMunicipality')}</Alert>}

      {lg && (
        <Stack spacing={2}>
          <HairlineCard sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                label={t('gov.team.history.officer')}
                value={officerId}
                onChange={(e) => setOfficerId(e.target.value)}
                sx={{ minWidth: 240 }}
                helperText={
                  officers.data && officers.data.length === 0
                    ? t('gov.team.history.noOfficers')
                    : undefined
                }
              >
                {(officers.data ?? []).map((o) => (
                  <MenuItem key={o.officerId} value={o.officerId}>
                    {o.officerName}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                type="date"
                label={t('gov.team.history.day')}
                value={date}
                onChange={(e) => setDate(e.target.value)}
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: { min: minDate, max: maxDate },
                }}
              />
            </Stack>
          </HairlineCard>

          {track.isError && <Alert severity="error">{t('gov.team.history.loadError')}</Alert>}
          {track.isLoading && officerId && <CircularProgress />}

          {track.data && (
            <>
              <HairlineCard sx={{ overflow: 'hidden', p: 0 }}>
                <Box
                  sx={{
                    height: { xs: 340, md: 460 },
                    '& .leaflet-container': {
                      height: '100%',
                      width: '100%',
                      background: palette.background.parch,
                    },
                  }}
                >
                  <MapContainer
                    bounds={NB_BOUNDS}
                    scrollWheelZoom
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      url={tiles.url}
                      attribution={tiles.attribution}
                      maxZoom={tiles.maxZoom}
                    />
                    <FitTrack points={points} />
                    <OpenOnScope hasPoints={points.length > 0} />
                    {points.length > 1 && (
                      <Polyline
                        positions={points.map((p) => [p.lat, p.lng] as [number, number])}
                        pathOptions={{ color: palette.brand.accent, weight: 3, opacity: 0.85 }}
                      />
                    )}
                    {track.data.visits.map((v) => {
                      const at = v.onSite ?? v.completed;
                      if (!at || at.lat === null || at.lng === null) return null;
                      return (
                        <Marker
                          key={v.visitId}
                          position={[at.lat, at.lng]}
                          icon={dotIcon(palette.semantic.warning, 16)}
                          title={v.reference}
                          alt={v.reference}
                        >
                          <Popup>
                            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                              {v.reference}
                            </Typography>
                            <Typography sx={{ fontSize: 13 }}>{v.address}</Typography>
                          </Popup>
                        </Marker>
                      );
                    })}
                    {current && (
                      <Marker
                        position={[current.lat, current.lng]}
                        icon={dotIcon(palette.brand.rail, 18)}
                        title={fmtTime(current.recordedAt)}
                        alt={fmtTime(current.recordedAt)}
                      />
                    )}
                  </MapContainer>
                </Box>
              </HairlineCard>

              <HairlineCard sx={{ p: 2 }}>
                {points.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('gov.team.history.emptyDay')}
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    <Typography id="replay-label" variant="overline">
                      {t('gov.team.history.scrubber')}
                    </Typography>
                    <Slider
                      aria-labelledby="replay-label"
                      min={0}
                      max={Math.max(0, points.length - 1)}
                      step={1}
                      value={index}
                      onChange={(_, v) => setIndex(Array.isArray(v) ? v[0] : v)}
                      getAriaValueText={(v) => fmtTime(replayAt(points, v)?.recordedAt ?? '')}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(v) => fmtTime(replayAt(points, v)?.recordedAt ?? '')}
                    />
                    <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">
                        {fmtTime(points[0].recordedAt)}
                      </Typography>
                      <Typography variant="body2" aria-live="polite">
                        {current ? fmtTime(current.recordedAt) : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fmtTime(points[points.length - 1].recordedAt)}
                      </Typography>
                    </Stack>
                  </Stack>
                )}
              </HairlineCard>

              <HairlineCard sx={{ p: 2 }}>
                <Typography variant="overline">{t('gov.team.history.visits')}</Typography>
                {track.data.visits.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('gov.team.history.noVisits')}
                  </Typography>
                ) : (
                  <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                    {track.data.visits.map((v) => (
                      <Stack
                        key={v.visitId}
                        component="li"
                        direction="row"
                        spacing={1}
                        sx={{ alignItems: 'center', py: 0.5 }}
                      >
                        <Link component={RouterLink} to={`/files/${v.fileId}`}>
                          {v.reference}
                        </Link>
                        <Typography variant="body2">{v.address}</Typography>
                        {v.onSite && (
                          <Typography variant="body2" color="text.secondary">
                            {t('gov.visits.step.on_site')} {fmtTime(v.onSite.at)}
                          </Typography>
                        )}
                        <StatusChip
                          status={v.status === 'completed' ? 'success' : 'neutral'}
                          label={t(`gov.visits.status.${v.status}`)}
                        />
                      </Stack>
                    ))}
                  </Box>
                )}
              </HairlineCard>
            </>
          )}
        </Stack>
      )}
    </AppShell>
  );
}
